import { createRoute, Link, useNavigate, useSearch } from "@tanstack/solid-router";
import { createSignal, Show, untrack } from "solid-js";
import { fromActorRef, useActorRef } from "@fleet/state-machines/solid";
import { authRoute } from "./route";
import { api, writeSessionToken } from "@/lib/api";
import { registerAccountFromResponse } from "@/lib/auth";
import { notifyTabAccountChanged, writeTabAccountId } from "@/lib/tab-account";
import { queryClient } from "@/lib/query-client";
import { primeSessionForAccount, sessionActor } from "@/lib/session-machine";
import {
  cloneSession,
  createAuthFlowHandlers,
  getAuthFlowMachineLogic,
  isValidSession,
  MFA_ERROR_CODES,
  queryKeys,
  VISITOR_ID,
} from "@time-tracker/shared";
import { useI18n } from "@/providers/i18n-provider";
import { usePowerSyncContext } from "@/providers/power-sync-context";

import { AlertCircle, Loader2 } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { msg } from "@lingui/core/macro";
import { AuthScene } from "@/components/auth/auth-scene";
import { Label, TextField } from "@fleet/ui";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import { AnimatedSection } from "@/components/ui/animated-section";
import { SocialAuthButtons } from "@/components/auth/social-auth-buttons";

/** Number of digits in a TOTP one-time code. */
const OTP_LENGTH = 6;

interface LoginPageInnerProps {
  db: AbstractPowerSyncDatabase;
}

function LoginPageInner(props: LoginPageInnerProps) {
  const { t } = useI18n();
  const search = useSearch({ from: loginRoute.id });
  const navigate = useNavigate();
  const { connectivityRef, disconnectAndClear, flushGuestUpgrade } = usePowerSyncContext();

  // Multi-account: when the switcher routes here in "add" mode (`?mode=add`),
  // we must NOT pre-sign-out the current account — the new one is ADDED
  // alongside it. The auth flow skips the pre-sign-out and we register the new
  // account in the registry via `onAccountAuthenticated`.
  const isAddingAccount = () => search().mode === "add";

  // oxlint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n): dev-only defaults
  const [email, setEmail] = createSignal(import.meta.env.DEV ? "admin@email.com" : "");
  // oxlint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n): dev-only defaults
  const [password, setPassword] = createSignal(import.meta.env.DEV ? "admin@email.com" : "");
  // 2FA challenge: the 6-digit TOTP code, entered after email+password verify.
  const [mfaCode, setMfaCode] = createSignal("");
  // Recovery affordance: submit a one-time backup code instead of a TOTP. The
  // backend's `verify-mfa` accepts EITHER in the same `otp` field, so toggling
  // this only relaxes the input shape/validation — the submit path is identical.
  const [useBackupCode, setUseBackupCode] = createSignal(false);

  const actorRef = useActorRef(
    untrack(() =>
      getAuthFlowMachineLogic({
        db: props.db,
        api,
        visitorId: VISITOR_ID,
        persistAuthToken: writeSessionToken,
        isAddingAccount: isAddingAccount(),
        onAccountAuthenticated: async (data) => {
          const account = await registerAccountFromResponse(data);
          if (account) {
            writeTabAccountId(account.userId, { silent: true });
            if (!isAddingAccount()) {
              await flushGuestUpgrade(account.userId);
            }
            primeSessionForAccount(account);
            notifyTabAccountChanged();
            void navigate({ to: search().redirect ?? "/" });
          }
        },
        disconnectAndClear: async () => {
          await disconnectAndClear();
        },
        refetchQueries: async (session) => {
          setEmail("");
          setPassword("");

          if (session) {
            // Proactively sync state to trigger the BootManager redirect immediately
            // without waiting for the background refetch to finish.
            sessionActor.send({
              type: "SERVER_REPLY",
              session: isValidSession(session) ? cloneSession(session) : null,
              error: null,
            });

            queryClient.setQueryData(queryKeys.auth.session(), session);
          }

          await queryClient.refetchQueries();
        },
        isWeb: true,
      }),
    ),
  );
  const state = fromActorRef(actorRef);
  const { send } = actorRef;
  const handlers = createAuthFlowHandlers(send);

  const connectivitySnapshot = fromActorRef(connectivityRef);
  const isServerDown = () => {
    const status = connectivitySnapshot().context.apiStatus;
    return status === "down" || status === "maintenance";
  };

  // The auth-flow machine is FLAT, so `state().value` is a plain string. The
  // real network work happens in `signingIn` (sign-out + sign-in API), reached
  // via the TRANSIENT `authenticating` state (`always`), which the machine
  // never rests in. Listing only `authenticating` here meant the button/fields
  // re-enabled during the actual server wait (double-submit, no feedback).
  // Treat every non-resting state as loading: only `idle`, `confirmingMerge`
  // (user decision, handled by the dialog) and `success` are not in-flight.
  const isLoading = () => {
    const { value } = state();
    return value !== "idle" && value !== "confirmingMerge" && value !== "success";
  };

  const isFormDisabled = () => isLoading() || isServerDown();
  const error = () => state().context.error;

  // The auth-flow machine stores a thrown actor's `error.message` verbatim. The
  // 2FA `verifyMfaActor` throws STABLE codes (not English text) precisely so we
  // can localize them HERE — both the wrong/expired TOTP and a wrong backup code
  // arrive as `MFA_INVALID_CODE`, plus the rate-limit case. Any other error
  // (e.g. the email/password path) passes through unchanged.
  const displayError = (raw: string | null): string | null => {
    switch (raw) {
      case MFA_ERROR_CODES.INVALID_CODE: {
        return t(msg`Invalid code. Please try again.`);
      }
      case MFA_ERROR_CODES.RATE_LIMITED: {
        return t(msg`Too many attempts. Please wait and try again.`);
      }
      case MFA_ERROR_CODES.UNEXPECTED: {
        return t(msg`An unexpected error occurred. Please try again.`);
      }
      default: {
        return raw;
      }
    }
  };

  // 2FA challenge UI: `mfaChallenge` rests waiting for the code; `verifyingMfa`
  // is the in-flight verify call. While the challenge is active we swap the
  // email/password form for the OTP step.
  const isMfaStep = () => {
    const { value } = state();
    return value === "mfaChallenge" || value === "verifyingMfa";
  };
  const isVerifyingMfa = () => state().value === "verifyingMfa";

  const handleSubmit = (e: SubmitEvent) => {
    e.preventDefault();
    handlers.handleSignIn(email(), password());
  };

  const handleMfaSubmit = (e: SubmitEvent) => {
    e.preventDefault();
    handlers.handleSubmitMfa(mfaCode());
  };

  const handleMfaCancel = () => {
    setMfaCode("");
    setUseBackupCode(false);
    handlers.handleCancel();
  };

  // Submit is enabled once the entered code is plausibly complete: a full TOTP
  // (6 digits) or a backup code (≥ 8 chars; they normalize to 10 server-side).
  const isMfaCodeReady = () => {
    const len = mfaCode().trim().length;
    return useBackupCode() ? len >= 8 : len >= OTP_LENGTH;
  };

  return (
    <AuthScene
      eyebrow={t(msg`Welcome back`)}
      headline={t(msg`Pick up where the clock left off.`)}
      tagline={t(msg`Sign in to keep tracking your focus, projects and hours — synced everywhere.`)}
      footer={
        <>
          <p class="text-sm text-muted-foreground">
            {t(msg`No account?`)}{" "}
            <Link
              to="/register"
              class="font-medium text-primary underline-offset-4 hover:underline"
            >
              {t(msg`Register`)}
            </Link>
          </p>
          <p class="text-xs text-muted-foreground/60">
            <Link to="/" class="transition-colors hover:text-primary">
              {t(msg`← Back to home`)}
            </Link>
          </p>
        </>
      }
    >
      <AnimatedSection show={isServerDown()}>
        <div class="mb-4 flex items-start gap-3 rounded-xl border border-warning/20 bg-warning/10 p-3">
          <AlertCircle class="mt-0.5 h-5 w-5 shrink-0 text-warning" />
          <p class="text-sm font-medium text-warning">
            {connectivitySnapshot().context.apiStatus === "maintenance"
              ? t(msg`The system is in maintenance. Please try again later.`)
              : t(msg`The server is not responding. Please try again later.`)}
          </p>
        </div>
      </AnimatedSection>

      <Show when={isMfaStep()}>
        <form onSubmit={handleMfaSubmit} class="space-y-5" data-testid="login-mfa-form">
          <div class="space-y-4">
            <p class="text-sm text-muted-foreground">
              {useBackupCode()
                ? t(msg`Enter one of your saved backup codes.`)
                : t(msg`Enter the 6-digit code from your authenticator app.`)}
            </p>
            <div class="space-y-2">
              <Label for="mfa-otp">
                {useBackupCode() ? t(msg`Backup code`) : t(msg`Verification code`)}
              </Label>
              <TextField
                id="mfa-otp"
                inputmode={useBackupCode() ? "text" : "numeric"}
                autocomplete="one-time-code"
                placeholder={useBackupCode() ? "xxxxx-xxxxx" : "000000"}
                required
                disabled={isVerifyingMfa()}
                value={mfaCode()}
                onInput={(e) => {
                  setMfaCode(e.currentTarget.value);
                }}
                data-testid="login-mfa-code"
              />
            </div>
            <button
              type="button"
              class="text-xs text-muted-foreground underline-offset-4 hover:text-primary hover:underline"
              disabled={isVerifyingMfa()}
              onClick={() => {
                setMfaCode("");
                setUseBackupCode((v) => !v);
              }}
              data-testid="login-mfa-toggle-backup"
            >
              {useBackupCode()
                ? t(msg`Use your authenticator app instead`)
                : t(msg`Use a backup code`)}
            </button>
            <AnimatedSection show={Boolean(error())}>
              <p class="text-sm text-destructive" data-testid="login-mfa-error">
                {displayError(error())}
              </p>
            </AnimatedSection>
          </div>

          <div class="flex flex-col gap-3">
            <Button
              type="submit"
              class="w-full"
              loading={isVerifyingMfa()}
              disabled={!isMfaCodeReady()}
              data-testid="login-mfa-verify"
            >
              {isVerifyingMfa() ? t(msg`Verifying…`) : t(msg`Verify`)}
            </Button>
            <Button
              type="button"
              variant="ghost"
              class="w-full"
              disabled={isVerifyingMfa()}
              onClick={handleMfaCancel}
              data-testid="login-mfa-cancel"
            >
              {t(msg`Back`)}
            </Button>
          </div>
        </form>
      </Show>

      <Show when={!isMfaStep()}>
        <form onSubmit={handleSubmit} class="space-y-4">
          <div class="space-y-2">
            <Label for="email">{t(msg`Email`)}</Label>
            <TextField
              id="email"
              type="email"
              placeholder={t(msg`your@email.com`)}
              autocomplete="email"
              required
              disabled={isFormDisabled()}
              value={email()}
              onInput={(e) => {
                setEmail(e.currentTarget.value);
              }}
            />
          </div>
          <div class="space-y-2">
            <div class="flex items-center justify-between">
              <Label for="password">{t(msg`Password`)}</Label>
              <Link
                to="/forgot-password"
                class="text-xs text-muted-foreground underline-offset-4 hover:text-primary hover:underline"
              >
                {t(msg`Forgot your password?`)}
              </Link>
            </div>
            <TextField
              id="password"
              type="password"
              placeholder={t(msg`••••••••`)}
              autocomplete="current-password"
              required
              disabled={isFormDisabled()}
              value={password()}
              onInput={(e) => {
                setPassword(e.currentTarget.value);
              }}
            />
          </div>
          <AnimatedSection show={Boolean(error())}>
            <p class="text-sm text-destructive">{error()}</p>
          </AnimatedSection>

          <Button type="submit" class="w-full" loading={isLoading()} disabled={isServerDown()}>
            {isLoading() ? t(msg`Signing in…`) : t(msg`Sign In`)}
          </Button>
          <SocialAuthButtons mode="login" redirect={search().redirect ?? "/"} />
        </form>
      </Show>

      <ConfirmDialog
        open={state().value === "confirmingMerge"}
        onOpenChange={(open) => {
          if (!open) {
            handlers.handleCancel();
          }
        }}
        title={t(msg`Keep local data?`)}
        description={t(
          msg`You have unsynced tasks created while offline. Do you want to keep them and merge them with your account, or discard them?`,
        )}
        confirmLabel={t(msg`Keep and merge`)}
        cancelLabel={t(msg`Discard`)}
        onConfirm={handlers.handleConfirmMerge}
        onCancel={handlers.handleConfirmDiscard}
      />
    </AuthScene>
  );
}

function LoginPage() {
  const { db, isLoading } = usePowerSyncContext();

  return (
    <Show
      when={!isLoading() && db()}
      fallback={
        <AuthScene>
          <div class="flex items-center justify-center py-12">
            <Loader2 class="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </AuthScene>
      }
    >
      {(database) => <LoginPageInner db={database()} />}
    </Show>
  );
}

export const loginRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/login",
  validateSearch: (search: Record<string, unknown>): { redirect?: string; mode?: "add" } => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    mode: search.mode === "add" ? "add" : undefined,
  }),
  component: LoginPage,
});
